import { config } from '../config';
import { captureSquare, fileToSquareCanvas, openCamera, stopCamera } from '../core/camera';
import type { CameraFacingMode } from '../core/camera';
import { loadPreparedData } from '../core/data';
import { hashImageData, PokemonMatcher } from '../core/matcher';
import { ModelClient } from '../core/model-client';
import { speakPokemon, stopSpeech } from '../core/speech';
import type { AppPhase, MatchResult } from '../types';

export class PokedexApp {
  private phase: AppPhase = 'booting';
  private stream: MediaStream | null = null;
  private matcher: PokemonMatcher | null = null;
  private modelClient: ModelClient | null = null;
  private scanTimer: number | null = null;
  private scanMessageIndex = 0;
  private lastPreviewUrl = '';
  private matches: MatchResult[] = [];
  private facingMode = config.camera.facingMode as CameraFacingMode;

  private readonly video: HTMLVideoElement;
  private readonly photo: HTMLImageElement;
  private readonly viewportPlaceholder: HTMLElement;
  private readonly scanOverlay: HTMLElement;
  private readonly statusText: HTMLElement;
  private readonly statusDetail: HTMLElement;
  private readonly progressBar: HTMLElement;
  private readonly primaryButton: HTMLButtonElement;
  private readonly cameraControls: HTMLElement;
  private readonly flipCameraButton: HTMLButtonElement;
  private readonly captureButton: HTMLButtonElement;
  private readonly uploadInput: HTMLInputElement;
  private readonly resultPanel: HTMLElement;
  private readonly resultImage: HTMLImageElement;
  private readonly resultNumber: HTMLElement;
  private readonly resultName: HTMLElement;
  private readonly resultEnglish: HTMLElement;
  private readonly resultCategory: HTMLElement;
  private readonly resultDescription: HTMLElement;
  private readonly resultTypes: HTMLElement;
  private readonly resultMeta: HTMLElement;
  private readonly resultScore: HTMLElement;
  private readonly candidates: HTMLElement;
  private readonly replayButton: HTMLButtonElement;
  private readonly retryButton: HTMLButtonElement;
  private readonly errorBox: HTMLElement;

  constructor(private readonly root: HTMLElement) {
    root.innerHTML = this.template();
    this.video = this.require<HTMLVideoElement>('#camera-video');
    this.photo = this.require<HTMLImageElement>('#captured-photo');
    this.viewportPlaceholder = this.require('#viewport-placeholder');
    this.scanOverlay = this.require('#scan-overlay');
    this.statusText = this.require('#status-text');
    this.statusDetail = this.require('#status-detail');
    this.progressBar = this.require('#progress-bar');
    this.primaryButton = this.require<HTMLButtonElement>('#start-button');
    this.cameraControls = this.require('#camera-controls');
    this.flipCameraButton = this.require<HTMLButtonElement>('#flip-camera-button');
    this.captureButton = this.require<HTMLButtonElement>('#capture-button');
    this.uploadInput = this.require<HTMLInputElement>('#upload-input');
    this.resultPanel = this.require('#result-panel');
    this.resultImage = this.require<HTMLImageElement>('#result-image');
    this.resultNumber = this.require('#result-number');
    this.resultName = this.require('#result-name');
    this.resultEnglish = this.require('#result-english');
    this.resultCategory = this.require('#result-category');
    this.resultDescription = this.require('#result-description');
    this.resultTypes = this.require('#result-types');
    this.resultMeta = this.require('#result-meta');
    this.resultScore = this.require('#result-score');
    this.candidates = this.require('#candidate-list');
    this.replayButton = this.require<HTMLButtonElement>('#replay-button');
    this.retryButton = this.require<HTMLButtonElement>('#retry-button');
    this.errorBox = this.require('#error-box');

    this.bindEvents();
    void this.boot();
  }

  private async boot(): Promise<void> {
    this.setPhase('booting');
    this.setStatus('正在读取本地图鉴', '检查资料与向量索引');
    try {
      const data = await loadPreparedData();
      this.matcher = new PokemonMatcher(data.embeddingFile, data.pokemon);
      this.setPhase('idle');
      this.setStatus('图鉴待机', `已载入 ${data.pokemon.length} 只宝可梦`);
    } catch (error) {
      this.fail(error);
    }
  }

  private bindEvents(): void {
    this.primaryButton.addEventListener('click', () => void this.startCamera());
    this.flipCameraButton.addEventListener('click', () => void this.flipCamera());
    this.captureButton.addEventListener('click', () => void this.captureAndScan());
    this.uploadInput.addEventListener('change', () => void this.handleUpload());
    this.replayButton.addEventListener('click', () => {
      const best = this.matches[0];
      if (best) speakPokemon(best.pokemon);
    });
    this.retryButton.addEventListener('click', () => void this.startCamera());
    window.addEventListener('beforeunload', () => this.dispose());
  }

  private async startCamera(): Promise<void> {
    if (!this.matcher) return;
    stopSpeech();
    stopCamera(this.stream);
    this.stream = null;
    this.clearError();
    this.resultPanel.hidden = true;
    this.photo.hidden = true;
    this.video.hidden = false;
    this.viewportPlaceholder.hidden = true;
    this.setPhase('camera');
    this.setStatus('正在申请摄像头权限', config.app.privacyText);

    try {
      this.stream = await openCamera(this.video, this.facingMode);
      this.setStatus('目标已进入扫描区域', '保持正面或选择一个有趣的姿势');
    } catch (error) {
      this.fail(error, true);
    }
  }

  private async flipCamera(): Promise<void> {
    if (this.phase !== 'camera') return;

    const previousFacingMode = this.facingMode;
    const nextFacingMode: CameraFacingMode = previousFacingMode === 'user' ? 'environment' : 'user';
    this.flipCameraButton.disabled = true;
    this.captureButton.disabled = true;
    this.setStatus('正在切换摄像头', nextFacingMode === 'user' ? '切换到前置摄像头' : '切换到后置摄像头');

    stopCamera(this.stream);
    this.stream = null;
    this.video.srcObject = null;

    try {
      this.stream = await openCamera(this.video, nextFacingMode);
      this.facingMode = nextFacingMode;
      this.setStatus('目标已进入扫描区域', '保持正面或选择一个有趣的姿势');
    } catch (error) {
      try {
        this.stream = await openCamera(this.video, previousFacingMode);
        this.setStatus('摄像头切换失败', '已恢复原摄像头');
      } catch {
        this.fail(error, true);
      }
    } finally {
      this.flipCameraButton.disabled = false;
      this.captureButton.disabled = false;
    }
  }

  private async captureAndScan(): Promise<void> {
    try {
      const mirror = config.camera.mirrorPreview && this.facingMode === 'user';
      const captured = captureSquare(this.video, mirror);
      stopCamera(this.stream);
      this.stream = null;
      await this.scan(captured.canvas, captured.previewUrl);
    } catch (error) {
      this.fail(error, true);
    }
  }

  private async handleUpload(): Promise<void> {
    const file = this.uploadInput.files?.[0];
    this.uploadInput.value = '';
    if (!file || !this.matcher) return;

    try {
      stopCamera(this.stream);
      this.stream = null;
      const captured = await fileToSquareCanvas(file);
      await this.scan(captured.canvas, captured.previewUrl);
    } catch (error) {
      this.fail(error, true);
    }
  }

  private async scan(canvas: HTMLCanvasElement, previewUrl: string): Promise<void> {
    if (!this.matcher) throw new Error('图鉴数据尚未准备完成。');
    stopSpeech();
    this.clearError();
    this.lastPreviewUrl = previewUrl;
    this.video.hidden = true;
    this.photo.src = previewUrl;
    this.photo.hidden = false;
    this.viewportPlaceholder.hidden = true;
    this.resultPanel.hidden = true;
    this.setPhase('loading-model');
    this.startScanMessages();

    try {
      const model = await this.ensureModel();
      this.setPhase('scanning');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('无法读取拍摄画面。');
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const imageHash = hashImageData(imageData.data);
      const embedding = await model.embed(imageData);
      const matches = this.matcher.search(embedding, imageHash);
      if (!matches.length) {
        throw new Error('没有找到稳定的匹配结果，请换一个角度或光线后重试。');
      }

      const elapsedFloor = config.ui.scanDurationMs;
      await new Promise((resolve) => window.setTimeout(resolve, elapsedFloor));
      this.matches = matches;
      this.stopScanMessages();
      this.renderResult(matches, 0);
      this.setPhase('result');
      this.setStatus('匹配完成', `最高匹配指数 ${matches[0]!.displayScore}%`);

      if (config.matching.autoSpeakBestResult) {
        speakPokemon(matches[0]!.pokemon);
      }
    } catch (error) {
      this.stopScanMessages();
      this.fail(error, true);
    }
  }

  private async ensureModel(): Promise<ModelClient> {
    if (!this.modelClient) {
      this.modelClient = new ModelClient();
      this.modelClient.onProgress((message, percent) => {
        this.setStatus('首次加载本地模型', message);
        this.progressBar.style.width = `${percent ?? 8}%`;
      });
    }
    const backend = await this.modelClient.ready();
    this.progressBar.style.width = '100%';
    this.setStatus('正在进行本地推理', `计算后端：${backend}`);
    return this.modelClient;
  }

  private renderResult(matches: MatchResult[], selectedIndex: number): void {
    const selected = matches[selectedIndex];
    if (!selected) return;
    const pokemon = selected.pokemon;

    this.resultImage.src = pokemon.image;
    this.resultImage.alt = pokemon.nameZh;
    this.resultNumber.textContent = `NO.${String(pokemon.id).padStart(4, '0')}`;
    this.resultName.textContent = pokemon.nameZh;
    this.resultEnglish.textContent = pokemon.nameEn.toUpperCase();
    this.resultCategory.textContent = pokemon.categoryZh;
    this.resultDescription.textContent = pokemon.descriptionZh;
    this.resultScore.textContent = `${selected.displayScore}%`;
    this.resultMeta.textContent = `身高 ${pokemon.heightM.toFixed(1)} m　体重 ${pokemon.weightKg.toFixed(1)} kg`;

    this.resultTypes.replaceChildren(
      ...pokemon.typesZh.map((type) => {
        const chip = document.createElement('span');
        chip.className = 'type-chip';
        chip.textContent = type;
        return chip;
      }),
    );

    this.candidates.replaceChildren(
      ...matches.map((match, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `candidate ${index === selectedIndex ? 'is-selected' : ''}`;
        button.innerHTML = `<span>${index + 1}</span><strong></strong><em>${match.displayScore}%</em>`;
        const strong = button.querySelector('strong');
        if (strong) strong.textContent = match.pokemon.nameZh;
        button.addEventListener('click', () => {
          this.renderResult(matches, index);
          speakPokemon(match.pokemon);
        });
        return button;
      }),
    );

    this.resultPanel.hidden = false;
  }

  private startScanMessages(): void {
    this.stopScanMessages();
    this.scanMessageIndex = 0;
    this.scanOverlay.dataset.active = 'true';
    this.scanTimer = window.setInterval(() => {
      const messages = config.ui.scanMessages;
      const message = messages[this.scanMessageIndex % messages.length];
      if (message) this.setStatus(message, '照片仅在当前设备中处理');
      this.scanMessageIndex += 1;
    }, 700);
  }

  private stopScanMessages(): void {
    if (this.scanTimer !== null) window.clearInterval(this.scanTimer);
    this.scanTimer = null;
    this.scanOverlay.dataset.active = 'false';
  }

  private setPhase(phase: AppPhase): void {
    this.phase = phase;
    this.root.dataset.phase = phase;
    this.primaryButton.disabled = phase === 'booting' || phase === 'loading-model' || phase === 'scanning';
    this.primaryButton.hidden = phase === 'camera';
    this.cameraControls.hidden = phase !== 'camera';
    this.captureButton.hidden = phase !== 'camera';
  }

  private setStatus(title: string, detail: string): void {
    this.statusText.textContent = title;
    this.statusDetail.textContent = detail;
  }

  private fail(error: unknown, recoverable = false): void {
    const message = error instanceof Error ? error.message : String(error);
    this.setPhase(recoverable ? 'idle' : 'error');
    this.setStatus('图鉴发生错误', message);
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
    if (recoverable) this.viewportPlaceholder.hidden = false;
  }

  private clearError(): void {
    this.errorBox.hidden = true;
    this.errorBox.textContent = '';
  }

  private dispose(): void {
    stopCamera(this.stream);
    stopSpeech();
    this.modelClient?.dispose();
  }

  private require<T extends Element = HTMLElement>(selector: string): T {
    const element = this.root.querySelector<T>(selector);
    if (!element) throw new Error(`缺少界面元素：${selector}`);
    return element;
  }

  private template(): string {
    return `
      <main class="page-shell">
        <section class="dex" aria-label="个人图鉴">
          <header class="dex-head">
            <div class="lens-cluster" aria-hidden="true">
              <span class="main-lens"></span><span></span><span></span><span></span>
            </div>
            <div class="brand">
              <p>${config.app.subtitle}</p>
              <h1>${config.app.title}</h1>
            </div>
            <div class="signal" aria-hidden="true"><i></i><i></i><i></i></div>
          </header>

          <div class="dex-body">
            <section class="left-column">
              <div class="viewport-frame">
                <div class="viewport">
                  <video id="camera-video" playsinline muted hidden></video>
                  <img id="captured-photo" alt="本次扫描照片" hidden />
                  <div id="viewport-placeholder" class="viewport-placeholder">
                    <svg viewBox="0 0 120 120" aria-hidden="true">
                      <circle cx="60" cy="60" r="42"></circle>
                      <path d="M34 92c6-20 18-29 26-29s20 9 26 29M45 48c0-11 6-20 15-20s15 9 15 20-6 18-15 18-15-7-15-18z"></path>
                    </svg>
                    <strong>等待扫描对象</strong>
                    <span>启动摄像头或从相册选择照片</span>
                  </div>
                  <div id="scan-overlay" class="scan-overlay" data-active="false" aria-hidden="true">
                    <span class="corner top-left"></span><span class="corner top-right"></span>
                    <span class="corner bottom-left"></span><span class="corner bottom-right"></span>
                    <i class="scan-line"></i>
                  </div>
                </div>
              </div>

              <div class="status-console" aria-live="polite">
                <div class="status-dot"></div>
                <div>
                  <strong id="status-text">系统启动中</strong>
                  <span id="status-detail">正在初始化</span>
                </div>
                <div class="progress-track"><i id="progress-bar"></i></div>
              </div>

              <div id="error-box" class="error-box" hidden></div>

              <div class="controls">
                <button id="start-button" class="primary-button" type="button">启动摄像头</button>
                <div id="camera-controls" class="camera-controls" hidden>
                  <button id="flip-camera-button" class="flip-camera-button" type="button" aria-label="切换前后摄像头" title="切换前后摄像头">↻</button>
                  <button id="capture-button" class="shutter-button" type="button" aria-label="拍摄并扫描"><i></i></button>
                </div>
                <label class="upload-button">
                  从相册选择
                  <input id="upload-input" type="file" accept="image/*" />
                </label>
              </div>
              <p class="privacy-note">${config.app.privacyText}</p>
            </section>

            <section id="result-panel" class="result-panel" hidden>
              <div class="result-screen">
                <div class="result-heading">
                  <span id="result-number">NO.0000</span>
                  <span class="match-label">MATCH <b id="result-score">--%</b></span>
                </div>
                <div class="pokemon-visual">
                  <div class="grid-floor"></div>
                  <img id="result-image" alt="匹配的宝可梦" />
                </div>
                <div class="pokemon-title">
                  <div>
                    <h2 id="result-name"></h2>
                    <p id="result-english"></p>
                  </div>
                  <div id="result-types" class="type-list"></div>
                </div>
                <p id="result-category" class="category"></p>
                <p id="result-meta" class="meta"></p>
                <p id="result-description" class="description"></p>
              </div>

              <div class="candidate-panel">
                <span class="panel-label">最接近的图鉴记录</span>
                <div id="candidate-list" class="candidate-list"></div>
              </div>

              <div class="result-actions">
                <button id="replay-button" type="button">重新播报</button>
                <button id="retry-button" type="button">再次扫描</button>
              </div>
            </section>
          </div>

          <footer class="dex-footer">
            <span>${config.app.unofficialNotice}</span>
            <span>DATA: POKEAPI · MODEL: CLIP</span>
          </footer>
        </section>
      </main>
    `;
  }
}
